import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { motion } from 'framer-motion';
import { 
  ArrowLeft, Wind, Smile, Eye, Anchor, 
  Play, Pause, X, Volume2, VolumeX,
  Heart, Brain, Zap, CloudRain, CircleDot, Bolt,
  Flame, Leaf, Phone, RotateCcw, Crown
} from 'lucide-react';
import FloatingHomeButton from '../../components/FloatingHomeButton';
import { trackActivity } from '../../components/ReclaimAssistant';
import { voiceVolume, musicVolume, getStoredAudioMode } from '../../utils/modeAudio';

const API_URL = process.env.REACT_APP_BACKEND_URL;

import {
  NATURE_IMAGE_LIBRARY,
  getTodaysNatureImages,
  RESET_VOICE_AUDIO,
  RESETS,
  SUGGESTIONS,
  GRID_ORDER,
  CATEGORY_TABS,
} from './resetsData';
import useTrackPhases from '../../hooks/useTrackPhases';
import SyncedPhrase from '../../components/SyncedPhrase';


const NetworkerResetsPage = () => {
  const navigate = useNavigate();
  const { resetId: urlResetId } = useParams();
  const [searchParams] = useSearchParams();
  const fromJourney = searchParams.get('fromJourney') === 'true';
  
  // Get journey return info from sessionStorage
  const getJourneyReturnInfo = () => {
    try {
      const data = sessionStorage.getItem('journeyResetReturn');
      return data ? JSON.parse(data) : null;
    } catch {
      return null;
    }
  };
  
  const [activeReset, setActiveReset] = useState(null);
  const [isPlaying, setIsPlaying] = useState(false);
  const [currentPhaseIndex, setCurrentPhaseIndex] = useState(0);
  const [elapsedTime, setElapsedTime] = useState(0);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [isTransitioning, setIsTransitioning] = useState(false);
  const [voiceEnabled, setVoiceEnabled] = useState(true);
  const [streak, setStreak] = useState(0);
  const [todayCount, setTodayCount] = useState(0);
  const [hasCompletedToday, setHasCompletedToday] = useState(false);
  const [resetCompleted, setResetCompleted] = useState(false);
  const [autoStarted, setAutoStarted] = useState(false);
  // Edge — filter the grid by Calm / Sleep / Performance
  const [categoryFilter, setCategoryFilter] = useState('all');
  
  const timerRef = useRef(null);
  const voiceAudioRef = useRef(null);
  const musicAudioRef = useRef(null);
  // Tracks the in-flight startReset() — if the user taps a different reset
  // tile while the 100ms setTimeout is still pending, we cancel the prior
  // one so two audio elements never spawn (root cause of the echo bug).
  const startTimeoutRef = useRef(null);

  // Hard-stop all audio on unmount — prevents the "back button leaves voice
  // playing" bug reported on the Aurum Daily Edge resets.
  useEffect(() => {
    return () => {
      if (startTimeoutRef.current) {
        clearTimeout(startTimeoutRef.current);
        startTimeoutRef.current = null;
      }
      try {
        if (voiceAudioRef.current) {
          voiceAudioRef.current.pause();
          voiceAudioRef.current.src = "";
          voiceAudioRef.current = null;
        }
      } catch (_) { /* noop */ }
      try {
        if (musicAudioRef.current) {
          musicAudioRef.current.pause();
          musicAudioRef.current.src = "";
          musicAudioRef.current = null;
        }
      } catch (_) { /* noop */ }
    };
  }, []);

  // Backstop — hard-stop audio on pagehide (tab close/reload), and SOFT pause
  // on visibilitychange→hidden so a brief notification check doesn't blow up
  // the session.
  useEffect(() => {
    const hardStop = () => {
      try { voiceAudioRef.current && (voiceAudioRef.current.pause(), voiceAudioRef.current.src = "") } catch (_) {}
      try { musicAudioRef.current && (musicAudioRef.current.pause(), musicAudioRef.current.src = "") } catch (_) {}
    };
    const softPause = () => {
      if (document.visibilityState !== 'hidden') return;
      try { voiceAudioRef.current && !voiceAudioRef.current.paused && voiceAudioRef.current.pause(); } catch (_) {}
      try { musicAudioRef.current && !musicAudioRef.current.paused && musicAudioRef.current.pause(); } catch (_) {}
    };
    window.addEventListener('pagehide', hardStop);
    document.addEventListener('visibilitychange', softPause);
    return () => {
      window.removeEventListener('pagehide', hardStop);
      document.removeEventListener('visibilitychange', softPause);
    };
  }, []);

  // Auto-start reset if coming from URL with resetId.
  // 0ms delay (was 500ms) so we never flash the selection grid.
  useEffect(() => {
    if (urlResetId && RESETS[urlResetId] && !autoStarted) {
      setAutoStarted(true);
      startReset(urlResetId);
    }
  }, [urlResetId, autoStarted]);

  const resetConfig = activeReset ? RESETS[activeReset] : null;

  // Whisper word-level override — when populated for this reset's track id
  // (resync_audio_batch.py runs Whisper word-by-word and writes to Mongo),
  // we use the override phrases for CapCut-style word reveal. Track ids
  // mirror the static-audio filename: e.g. `reset_breathe`, `reset_pre_call`.
  //
  // Whisper word-level override — when populated for this reset's track id,
  // we use the override phrases for CapCut-style word-by-word reveal.
  // The hardcoded `RESETS[id].phases` are themselves derived from earlier
  // Whisper runs (see comments in resetsData.js), so the override is just
  // the latest take with word-level granularity. Falls back to the
  // hardcoded list if no override exists yet (e.g. `bathe`).
  const overrideTrackId = activeReset ? `reset_${activeReset}` : null;
  const phaseOverride = useTrackPhases(overrideTrackId, { enabled: !!overrideTrackId });
  const livePhrases = (phaseOverride.phrases && phaseOverride.phrases.length)
    ? phaseOverride.phrases
    : (resetConfig?.phases || []);

  // When complete, always show the closing message from the last phase
  const currentPhase = livePhrases[currentPhaseIndex] || null;
  const nextPhraseStart = livePhrases[currentPhaseIndex + 1]?.start ?? null;
  const isComplete = resetCompleted || (resetConfig && elapsedTime >= resetConfig.audioDuration);

  // Get the display text - use closing message when complete
  const displayText = isComplete
    ? resetConfig?.closingMessage
    : (currentPhase?.text || '');

  // Load streak data on mount
  useEffect(() => {
    const loadStreak = () => {
      try {
        const streakData = JSON.parse(localStorage.getItem('resetStreak') || '{}');
        const today = new Date().toDateString();
        const yesterday = new Date(Date.now() - 86400000).toDateString();
        
        // Check if streak is still valid
        if (streakData.lastDate === today) {
          setStreak(streakData.count || 0);
          setTodayCount(streakData.todayCount || 0);
          setHasCompletedToday(true);
        } else if (streakData.lastDate === yesterday) {
          setStreak(streakData.count || 0);
          setTodayCount(0);
          setHasCompletedToday(false);
        } else {
          // Streak broken - reset
          setStreak(0);
          setTodayCount(0);
          setHasCompletedToday(false);
        }
      } catch (e) {
        console.log('[Reset] Error loading streak:', e);
      }
    };
    loadStreak();
  }, []);

  // Track reset completion
  const recordCompletion = (resetId) => {
    const today = new Date().toDateString();
    const streakData = JSON.parse(localStorage.getItem('resetStreak') || '{}');
    const yesterday = new Date(Date.now() - 86400000).toDateString();
    
    let newStreak = streak;
    let newTodayCount = todayCount + 1;
    
    // If first completion today, increment streak
    if (!hasCompletedToday) {
      if (streakData.lastDate === yesterday || streakData.lastDate === today) {
        newStreak = (streakData.count || 0) + 1;
      } else {
        newStreak = 1; // Starting new streak
      }
      setHasCompletedToday(true);
    }
    
    // Save to localStorage
    const newData = {
      count: newStreak,
      lastDate: today,
      todayCount: newTodayCount,
      lastReset: resetId
    };
    localStorage.setItem('resetStreak', JSON.stringify(newData));
    
    setStreak(newStreak);
    setTodayCount(newTodayCount);
  };

  // Find current phase based on elapsed time.
  //
  // SYNC FIX (Apr 30, 2026): fire the visual ~250ms BEFORE the configured
  // `start` time. This compensates for the small perceptual delay between
  // (a) a text element fading in and (b) the ear registering Michael's
  // voice. Without this, users feel the text is "slightly behind" the
  // audio even when the config matches the speech onset perfectly.
  // 250ms is well inside the "comfortable read-ahead" window for a
  // meditative pace — the eye arrives just as the voice lands.
  const PHASE_LEAD_SECONDS = 0.25;
  useEffect(() => {
    if (!resetConfig || !isPlaying || resetCompleted) return;

    const phases = livePhrases;
    if (!phases || !phases.length) return;
    const effective = elapsedTime + PHASE_LEAD_SECONDS;
    for (let i = phases.length - 1; i >= 0; i--) {
      if (effective >= phases[i].start) {
        if (i !== currentPhaseIndex) {
          setCurrentPhaseIndex(i);
        }
        break;
      }
    }
  }, [elapsedTime, resetConfig, livePhrases, isPlaying, currentPhaseIndex, resetCompleted]);

  // Timer to track elapsed time - SYNCED WITH AUDIO (waits for audio to start)
  useEffect(() => {
    if (isPlaying && !isComplete) {
      let audioStarted = false;
      let waitingForAudio = true;
      
      timerRef.current = setInterval(() => {
        // Check if audio has started playing
        if (voiceAudioRef.current && voiceAudioRef.current.currentTime > 0.1) {
          audioStarted = true;
          waitingForAudio = false;
          const audioTime = voiceAudioRef.current.currentTime;
          setElapsedTime(audioTime);
          
          if (audioTime >= resetConfig.audioDuration - 0.1) {
            setIsPlaying(false);
            setResetCompleted(true);
            clearInterval(timerRef.current);
          }
        } else if (!audioStarted && waitingForAudio) {
          // Still waiting for audio to start - keep elapsed at 0
          // This prevents text from showing before audio plays
          setElapsedTime(0);
        }
      }, 50);
      
      // After 2 seconds, if audio hasn't started, assume it's blocked and use fallback
      const fallbackTimeout = setTimeout(() => {
        if (!audioStarted) {
          waitingForAudio = false;
          // Start fallback timer
          if (timerRef.current) clearInterval(timerRef.current);
          timerRef.current = setInterval(() => {
            if (voiceAudioRef.current && voiceAudioRef.current.currentTime > 0.1) {
              // Audio started late, sync to it
              setElapsedTime(voiceAudioRef.current.currentTime);
            } else {
              setElapsedTime(prev => {
                const newTime = prev + 0.05;
                if (newTime >= resetConfig.audioDuration) {
                  setIsPlaying(false);
                  setResetCompleted(true);
                  clearInterval(timerRef.current);
                  return resetConfig.audioDuration;
                }
                return newTime;
              });
            }
          }, 50);
        }
      }, 2000);
      
      return () => {
        clearTimeout(fallbackTimeout);
        if (timerRef.current) clearInterval(timerRef.current);
      };
    } else {
      if (timerRef.current) clearInterval(timerRef.current);
    }
    
    return () => {
      if (timerRef.current) clearInterval(timerRef.current);
    };
  }, [isPlaying, isComplete, resetConfig]);

  // Auto-redirect to Journey after reset completes (if from Journey)
  useEffect(() => {
    if (isComplete && fromJourney && activeReset) {
      const journeyInfo = getJourneyReturnInfo();
      if (journeyInfo) {
        // Small delay to show completion state, then auto-redirect
        const redirectTimer = setTimeout(() => {
          recordCompletion(activeReset);
          sessionStorage.removeItem('journeyResetReturn');
          navigate(`${journeyInfo.returnPath}?resetComplete=true`);
        }, 2000); // 2 second delay to show "Reset Complete" message
        
        return () => clearTimeout(redirectTimer);
      }
    }
  }, [isComplete, fromJourney, activeReset, navigate]);

  // Start a reset
  const startReset = (resetId) => {
    // Cancel any in-flight startReset setTimeout — prevents the echo bug
    // when the user double-taps a tile (or taps a second tile within the
    // 100ms transition window) and two audio sessions spawn.
    if (startTimeoutRef.current) {
      clearTimeout(startTimeoutRef.current);
      startTimeoutRef.current = null;
    }
    // Always tear down any in-flight audio before starting fresh — prevents
    // the echo bug when the user taps a new reset while the previous one is
    // still playing.
    if (voiceAudioRef.current) {
      try {
        voiceAudioRef.current.pause();
        voiceAudioRef.current.onended = null;
        voiceAudioRef.current.onplay = null;
        voiceAudioRef.current.onerror = null;
        voiceAudioRef.current.src = "";
        voiceAudioRef.current.load();
      } catch (_) { /* noop */ }
      voiceAudioRef.current = null;
    }
    if (musicAudioRef.current) {
      try {
        musicAudioRef.current.pause();
        musicAudioRef.current.src = "";
        musicAudioRef.current.load();
      } catch (_) { /* noop */ }
      musicAudioRef.current = null;
    }

    setIsTransitioning(true);
    setActiveReset(resetId);
    setCurrentPhaseIndex(0);
    setElapsedTime(0);
    setResetCompleted(false);
    
    startTimeoutRef.current = setTimeout(() => {
      startTimeoutRef.current = null;
      setIsTransitioning(false);
      
      if (voiceEnabled && RESETS[resetId].voiceAudio) {
        // All voice files are served as .m4a directly — the legacy MP3 mirror
        // is no longer used. The previous MP3→m4a fallback caused an echo bug
        // where the fallback `.play()` could fire AFTER the user had already
        // tapped Stop, kicking off a second audio session in parallel.
        const audioUrl = `${API_URL}${RESETS[resetId].voiceAudio}`;
        const audio = new Audio(audioUrl);
        // Voice volume respects the user's Clear / Soft / Deep choice
        const VOICE_TARGET = voiceVolume(getStoredAudioMode());
        audio.volume = VOICE_TARGET;
        voiceAudioRef.current = audio;

        // Gentle background music so resets aren't bare voice — user feedback
        // May 2026: "Many resets — music did not play at all". The voice is
        // the primary layer (respects mode); music sits under it at ~20%.
        try {
          const API_BASE = process.env.REACT_APP_BACKEND_URL || "";
          const bg = new Audio(`${API_BASE}/api/audio/ambient/calming_piano.mp3`);
          bg.loop = true;
          bg.volume = 0;
          bg.play().then(() => {
            const MUSIC_TARGET = musicVolume(getStoredAudioMode());
            let v = 0;
            const fade = setInterval(() => {
              v += 0.004;
              if (v >= MUSIC_TARGET || !musicAudioRef.current) {
                clearInterval(fade);
                if (musicAudioRef.current) musicAudioRef.current.volume = MUSIC_TARGET;
              } else {
                bg.volume = v;
              }
            }, 90);
          }).catch(() => {});
          musicAudioRef.current = bg;
        } catch (_) { /* music is optional — voice is the headline */ }
        
        // Start timer only when audio actually starts playing
        audio.onplay = () => {
          setIsPlaying(true);
        };
        
        audio.onended = () => {
          setIsPlaying(false);
          setResetCompleted(true);
          setElapsedTime(RESETS[resetId].audioDuration);
          // Track reset completion for smart suggestions
          trackActivity('reset_completed', { resetId });
        };
        
        audio.play().catch(err => {
          console.log('[Reset] Voice autoplay blocked:', err);
          // If autoplay blocked, show play button but don't start timer
          // User needs to tap play to start
          setIsPlaying(false);
        });
      } else {
        setIsPlaying(true);
      }
    }, 100);
  };

  // Handle manual play button tap (important for iOS)
  const handleManualPlay = () => {
    if (voiceAudioRef.current && !isPlaying) {
      voiceAudioRef.current.play().then(() => {
        setIsPlaying(true);
      }).catch(err => {
        console.log('[Reset] Manual play failed:', err);
        // Start without audio as last resort
        setIsPlaying(true);
      });
    } else {
      togglePlayPause();
    }
  };

  // Close reset
  const closeReset = () => {
    setIsPlaying(false);
    setResetCompleted(false);
    if (timerRef.current) clearInterval(timerRef.current);
    if (startTimeoutRef.current) {
      clearTimeout(startTimeoutRef.current);
      startTimeoutRef.current = null;
    }
    if (voiceAudioRef.current) {
      // Hard tear-down — pause + clear src + load() prevents ghost playback
      // resuming after a re-start (the echo bug from May 2026).
      try {
        voiceAudioRef.current.pause();
        voiceAudioRef.current.onended = null;
        voiceAudioRef.current.onplay = null;
        voiceAudioRef.current.onerror = null;
        voiceAudioRef.current.src = "";
        voiceAudioRef.current.load();
      } catch (_) { /* noop */ }
      voiceAudioRef.current = null;
    }
    if (musicAudioRef.current) {
      // IMMEDIATE hard-stop instead of a fade-out — prevents the ghost-music
      // bug where the user hits Home during the fade and the setInterval
      // keeps running after the component unmounts, leaking music onto the
      // home screen for ~1.5s.
      try {
        musicAudioRef.current.pause();
        musicAudioRef.current.src = "";
        musicAudioRef.current.load();
      } catch (_) { /* noop */ }
      musicAudioRef.current = null;
    }
    setActiveReset(null);
    setCurrentPhaseIndex(0);
    setElapsedTime(0);
    setIsTransitioning(false);
    
    // If coming from Journey, navigate back
    if (fromJourney) {
      const journeyInfo = getJourneyReturnInfo();
      if (journeyInfo) {
        sessionStorage.removeItem('journeyResetReturn');
        navigate(`${journeyInfo.returnPath}?resetComplete=true`);
      }
    }
  };
  
  // Toggle play/pause
  const togglePlayPause = () => {
    if (isPlaying) {
      setIsPlaying(false);
      if (voiceAudioRef.current) voiceAudioRef.current.pause();
      if (musicAudioRef.current) musicAudioRef.current.pause();
    } else {
      setIsPlaying(true);
      if (voiceAudioRef.current) voiceAudioRef.current.play().catch(() => {});
      if (musicAudioRef.current) musicAudioRef.current.play().catch(() => {});
    }
  };

  // Toggle voice
  const toggleVoice = () => {
    setVoiceEnabled(!voiceEnabled);
    if (voiceAudioRef.current) {
      if (voiceEnabled) {
        voiceAudioRef.current.pause();
      } else {
        voiceAudioRef.current.play().catch(() => {});
      }
    }
  };

  // Render the BIG immersive motion animation based on reset type
  const renderMotionAnimation = () => {
    if (!resetConfig) return null;
    const { motionType, accentColor, glowColor } = resetConfig;
    const animation = currentPhase?.animation || 'neutral';
    
    switch (motionType) {
      // SIMPLE BREATHE - Exact same animation as Daily Ritual
      case 'simple-breathe':
        // Map reset animation phases to breath phases
        const breathPhase = animation === 'expand' ? 'inhale' : 
                           animation === 'hold' ? 'hold' : 
                           animation === 'contract' ? 'release' : 'rest';
        return (
          <div className="absolute inset-0 flex items-center justify-center overflow-hidden">
            <div className="flex flex-col items-center">
              {/* Breathing circle - exact same as Daily Ritual */}
              <motion.div
                animate={{
                  scale: breathPhase === "inhale" ? 1.5 : 
                         breathPhase === "hold" ? 1.5 : 
                         breathPhase === "release" ? 1 : 
                         1,
                  boxShadow: breathPhase === "inhale" 
                    ? "0 0 60px 25px rgba(100, 160, 220, 0.25)"
                    : breathPhase === "hold"
                    ? "0 0 80px 35px rgba(100, 160, 220, 0.35)"
                    : breathPhase === "release"
                    ? "0 0 30px 10px rgba(100, 160, 220, 0.15)"
                    : "0 0 20px 5px rgba(100, 160, 220, 0.1)",
                }}
                transition={{ duration: 7, ease: "easeInOut" }}
                className="w-40 h-40 rounded-full bg-[#E3F0FD]/10 flex items-center justify-center"
              >
                <motion.div
                  animate={{
                    scale: breathPhase === "inhale" ? 1.4 : 
                           breathPhase === "hold" ? 1.4 : 
                           breathPhase === "release" ? 1 : 
                           0.9,
                    backgroundColor: breathPhase === "hold" 
                      ? "rgba(100, 160, 220, 0.3)" 
                      : breathPhase === "rest"
                      ? "rgba(227, 240, 253, 0.1)"
                      : "rgba(227, 240, 253, 0.2)",
                  }}
                  transition={{ duration: 7, ease: "easeInOut" }}
                  className="w-24 h-24 rounded-full"
                />
              </motion.div>
            </div>
          </div>
        );

      // ORIGINAL BREATHE - Simple concentric purple circles that pulse smoothly with breath
      case 'original-breathe':
        return (
          <div className="absolute inset-0 flex items-center justify-center overflow-hidden">
            {/* Outer ring - pulses smoothly */}
            <div 
              className="absolute rounded-full"
              style={{ 
                width: animation === 'expand' ? '420px' : animation === 'hold' ? '420px' : animation === 'contract' ? '180px' : '280px',
                height: animation === 'expand' ? '420px' : animation === 'hold' ? '420px' : animation === 'contract' ? '180px' : '280px',
                opacity: animation === 'expand' ? 0.5 : animation === 'hold' ? 0.55 : animation === 'contract' ? 0.25 : 0.35,
                background: 'radial-gradient(circle, transparent 55%, rgba(139, 92, 246, 0.35) 70%, transparent 85%)',
                transition: animation === 'expand' ? 'all 5s cubic-bezier(0.4, 0, 0.2, 1)' : 
                           animation === 'hold' ? 'all 0.5s ease' :
                           animation === 'contract' ? 'all 5s cubic-bezier(0.4, 0, 0.2, 1)' : 
                           'all 2s cubic-bezier(0.4, 0, 0.2, 1)'
              }}
            />
            {/* Middle ring - pulses with breath */}
            <div 
              className="absolute rounded-full"
              style={{ 
                width: animation === 'expand' ? '320px' : animation === 'hold' ? '320px' : animation === 'contract' ? '130px' : '200px',
                height: animation === 'expand' ? '320px' : animation === 'hold' ? '320px' : animation === 'contract' ? '130px' : '200px',
                opacity: animation === 'expand' ? 0.6 : animation === 'hold' ? 0.65 : animation === 'contract' ? 0.35 : 0.45,
                background: 'radial-gradient(circle, transparent 45%, rgba(167, 139, 250, 0.45) 65%, transparent 80%)',
                transition: animation === 'expand' ? 'all 5s cubic-bezier(0.4, 0, 0.2, 1)' : 
                           animation === 'hold' ? 'all 0.5s ease' :
                           animation === 'contract' ? 'all 5s cubic-bezier(0.4, 0, 0.2, 1)' : 
                           'all 2s cubic-bezier(0.4, 0, 0.2, 1)'
              }}
            />
            {/* Inner filled circle - the main visual */}
            <div 
              className="absolute rounded-full"
              style={{ 
                width: animation === 'expand' ? '220px' : animation === 'hold' ? '220px' : animation === 'contract' ? '80px' : '130px',
                height: animation === 'expand' ? '220px' : animation === 'hold' ? '220px' : animation === 'contract' ? '80px' : '130px',
                background: 'radial-gradient(circle, rgba(167, 139, 250, 0.8) 0%, rgba(139, 92, 246, 0.6) 50%, rgba(99, 102, 241, 0.4) 100%)',
                boxShadow: animation === 'expand' || animation === 'hold' 
                  ? '0 0 60px 30px rgba(139, 92, 246, 0.4), 0 0 120px 60px rgba(139, 92, 246, 0.2)' 
                  : '0 0 40px 20px rgba(139, 92, 246, 0.3)',
                transition: animation === 'expand' ? 'all 5s cubic-bezier(0.4, 0, 0.2, 1)' : 
                           animation === 'hold' ? 'all 0.5s ease' :
                           animation === 'contract' ? 'all 5s cubic-bezier(0.4, 0, 0.2, 1)' : 
                           'all 2s cubic-bezier(0.4, 0, 0.2, 1)'
              }}
            />
          </div>
        );

      // BREATHE - Big pulsing circle that syncs with inhale/exhale with continuous pulse
      case 'breathe-pulse':
        return (
          <div className="absolute inset-0 flex items-center justify-center overflow-hidden">
            {/* Outer glow ring with continuous pulse */}
            <div 
              className={`absolute rounded-full transition-all ease-in-out animate-breathe-glow
                ${animation === 'expand' ? 'w-[500px] h-[500px] opacity-40 duration-[4000ms]' : 
                  animation === 'hold' ? 'w-[480px] h-[480px] opacity-45 duration-[1000ms]' : 
                  animation === 'contract' ? 'w-[200px] h-[200px] opacity-20 duration-[5000ms]' : 
                  'w-[300px] h-[300px] opacity-30 duration-[2000ms]'}`}
              style={{ 
                background: `radial-gradient(circle, transparent 40%, ${accentColor} 60%, transparent 70%)`,
                boxShadow: `0 0 120px 60px ${glowColor}, 0 0 200px 100px ${accentColor}`
              }}
            />
            {/* Main breathing circle with continuous pulse */}
            <div 
              className={`absolute rounded-full transition-all ease-in-out animate-breathe-pulse
                ${animation === 'expand' ? 'w-[380px] h-[380px] duration-[4000ms]' : 
                  animation === 'hold' ? 'w-[360px] h-[360px] duration-[1000ms]' : 
                  animation === 'contract' ? 'w-[140px] h-[140px] duration-[5000ms]' : 
                  'w-[220px] h-[220px] duration-[2000ms]'}`}
              style={{ 
                background: `radial-gradient(circle, ${glowColor} 0%, ${accentColor} 40%, transparent 70%)`,
                boxShadow: `0 0 80px 40px ${accentColor}`
              }}
            />
            {/* Inner core with gentle pulse */}
            <div 
              className={`absolute rounded-full transition-all ease-in-out animate-breathe-core
                ${animation === 'expand' ? 'w-[180px] h-[180px] duration-[4000ms]' : 
                  animation === 'hold' ? 'w-[170px] h-[170px] duration-[1000ms]' : 
                  animation === 'contract' ? 'w-[60px] h-[60px] duration-[5000ms]' : 
                  'w-[100px] h-[100px] duration-[2000ms]'}`}
              style={{ 
                background: `radial-gradient(circle, rgba(255,255,255,0.8) 0%, ${glowColor} 50%, transparent 70%)`
              }}
            />
          </div>
        );
        
      // SMILE - Warm radiating sun that expands outward
      case 'radiate':
        return (
          <div className="absolute inset-0 flex items-center justify-center overflow-hidden">
            {/* Radiating rings */}
            {[...Array(5)].map((_, i) => (
              <div 
                key={i}
                className={`absolute rounded-full animate-radiate-ring
                  ${animation === 'radiate' || animation === 'expand' ? 'opacity-40' : 'opacity-20'}`}
                style={{ 
                  background: `radial-gradient(circle, transparent 60%, ${accentColor} 70%, transparent 80%)`,
                  width: '400px',
                  height: '400px',
                  animationDelay: `${i * 1.5}s`,
                  animationDuration: animation === 'radiate' ? '4s' : '6s'
                }}
              />
            ))}
            {/* Central warm glow */}
            <div 
              className={`absolute rounded-full transition-all duration-[2000ms] ease-out
                ${animation === 'glow' || animation === 'radiate' ? 'w-[300px] h-[300px] opacity-60' : 
                  animation === 'expand' ? 'w-[350px] h-[350px] opacity-50' : 
                  'w-[200px] h-[200px] opacity-30'}`}
              style={{ 
                background: `radial-gradient(circle, ${glowColor} 0%, ${accentColor} 40%, transparent 70%)`,
                boxShadow: `0 0 100px 50px ${accentColor}`
              }}
            />
            {/* Bright center */}
            <div 
              className={`absolute rounded-full transition-all duration-[1500ms]
                ${animation === 'glow' ? 'w-[80px] h-[80px] opacity-80' : 'w-[50px] h-[50px] opacity-50'}`}
              style={{ 
                background: 'radial-gradient(circle, white 0%, rgba(251, 191, 36, 0.8) 50%, transparent 70%)'
              }}
            />
          </div>
        );
        
      // EXPAND - Infinite ripples expanding from center
      case 'ripple-expand':
        return (
          <div className="absolute inset-0 flex items-center justify-center overflow-hidden">
            {/* Multiple expanding ripples */}
            {[...Array(6)].map((_, i) => (
              <div 
                key={i}
                className={`absolute rounded-full animate-ripple-expand
                  ${animation === 'expand' || animation === 'expand-wide' ? 'opacity-50' : 'opacity-25'}`}
                style={{ 
                  border: `2px solid ${accentColor}`,
                  boxShadow: `0 0 20px ${accentColor}`,
                  width: '100px',
                  height: '100px',
                  animationDelay: `${i * 1.2}s`,
                  animationDuration: animation === 'expand-wide' ? '5s' : '7s'
                }}
              />
            ))}
            {/* Central awareness point */}
            <div 
              className={`absolute rounded-full transition-all duration-[3000ms] ease-out
                ${animation === 'expand-wide' ? 'w-[150px] h-[150px]' : 
                  animation === 'expand' ? 'w-[100px] h-[100px]' : 
                  'w-[60px] h-[60px]'}`}
              style={{ 
                background: `radial-gradient(circle, ${glowColor} 0%, ${accentColor} 50%, transparent 70%)`,
                boxShadow: `0 0 60px 30px ${accentColor}`
              }}
            />
          </div>
        );
        
      // DROP - Particles settling downward like gravity
      case 'settle-particles':
        return (
          <div className="absolute inset-0 overflow-hidden">
            {/* Settling particles */}
            {[...Array(20)].map((_, i) => (
              <div
                key={i}
                className={`absolute rounded-full animate-settle-drop
                  ${animation === 'gravity' || animation === 'deep-drop' ? 'opacity-70' : 'opacity-40'}`}
                style={{
                  background: `radial-gradient(circle, ${glowColor} 0%, ${accentColor} 70%)`,
                  width: `${8 + (i % 4) * 4}px`,
                  height: `${8 + (i % 4) * 4}px`,
                  left: `${5 + i * 4.5}%`,
                  top: `-10%`,
                  animationDelay: `${(i * 0.3) % 4}s`,
                  animationDuration: `${5 + (i % 3) * 2}s`
                }}
              />
            ))}
            {/* Ground glow */}
            <div 
              className={`absolute bottom-0 left-0 right-0 transition-all duration-[2000ms]
                ${animation === 'ground' || animation === 'gravity' || animation === 'deep-drop' 
                  ? 'h-[150px] opacity-60' : 'h-[80px] opacity-30'}`}
              style={{ 
                background: `linear-gradient(to top, ${accentColor}, transparent)`
              }}
            />
            {/* Central grounding energy */}
            <div className="absolute bottom-1/4 left-1/2 -translate-x-1/2">
              <div 
                className={`rounded-full transition-all duration-[2000ms]
                  ${animation === 'deep-drop' ? 'w-[200px] h-[200px] opacity-50' : 
                    animation === 'settle' ? 'w-[150px] h-[150px] opacity-40' : 
                    'w-[100px] h-[100px] opacity-25'}`}
                style={{ 
                  background: `radial-gradient(circle, ${glowColor} 0%, ${accentColor} 50%, transparent 70%)`
                }}
              />
            </div>
          </div>
        );
        
      // STILL - Subtle ethereal shimmer with minimal movement
      case 'shimmer-still':
        return (
          <div className="absolute inset-0 flex items-center justify-center overflow-hidden">
            {/* Subtle shimmer background */}
            <div 
              className={`absolute inset-0 animate-shimmer-subtle
                ${animation === 'still' || animation === 'rest' ? 'opacity-20' : 'opacity-10'}`}
              style={{ 
                background: `radial-gradient(ellipse at center, ${accentColor} 0%, transparent 70%)`
              }}
            />
            {/* Central stillness point */}
            <div 
              className={`absolute rounded-full transition-all duration-[4000ms] ease-in-out
                ${animation === 'still' || animation === 'rest' || animation === 'observe' 
                  ? 'w-[120px] h-[120px] opacity-40' : 
                  animation === 'expand' ? 'w-[180px] h-[180px] opacity-30' :
                  animation === 'contract' ? 'w-[60px] h-[60px] opacity-20' :
                  'w-[80px] h-[80px] opacity-25'}`}
              style={{ 
                background: `radial-gradient(circle, ${glowColor} 0%, ${accentColor} 50%, transparent 70%)`,
                boxShadow: `0 0 60px 30px ${accentColor}`
              }}
            />
            {/* Barely perceptible pulse */}
            <div 
              className={`absolute rounded-full animate-stillness-pulse
                ${animation === 'still' ? 'opacity-30' : 'opacity-0'}`}
              style={{ 
                width: '200px',
                height: '200px',
                border: `1px solid ${accentColor}`
              }}
            />
          </div>
        );
        
      // ACTIVATE - Rising energy bolts/flames upward
      case 'energize-rise':
        return (
          <div className="absolute inset-0 overflow-hidden">
            {/* Rising energy columns */}
            {[...Array(12)].map((_, i) => (
              <div
                key={i}
                className={`absolute animate-energy-rise
                  ${animation === 'surge' || animation === 'activated' || animation === 'energize' 
                    ? 'opacity-80' : animation === 'power-in' || animation === 'power-out' 
                    ? 'opacity-60' : 'opacity-30'}`}
                style={{
                  background: `linear-gradient(to top, transparent, ${accentColor}, ${glowColor}, transparent)`,
                  width: `${6 + (i % 3) * 3}px`,
                  height: `${80 + (i % 4) * 30}px`,
                  left: `${8 + i * 7.5}%`,
                  bottom: `0`,
                  borderRadius: '50% 50% 0 0',
                  animationDelay: `${(i * 0.15) % 2}s`,
                  animationDuration: animation === 'surge' || animation === 'activated' ? '1s' : '2s'
                }}
              />
            ))}
            {/* Central power core */}
            <div className="absolute bottom-1/3 left-1/2 -translate-x-1/2">
              <div 
                className={`rounded-full transition-all duration-[500ms]
                  ${animation === 'activated' ? 'w-[250px] h-[250px] opacity-70' : 
                    animation === 'surge' || animation === 'energize' ? 'w-[200px] h-[200px] opacity-60' : 
                    animation === 'power-in' ? 'w-[180px] h-[180px] opacity-50' :
                    animation === 'power-out' ? 'w-[120px] h-[120px] opacity-40' :
                    'w-[100px] h-[100px] opacity-30'}`}
                style={{ 
                  background: `radial-gradient(circle, ${glowColor} 0%, ${accentColor} 50%, transparent 70%)`,
                  boxShadow: animation === 'activated' || animation === 'surge' 
                    ? `0 0 100px 50px ${glowColor}, 0 0 200px 100px ${accentColor}` 
                    : `0 0 50px 25px ${accentColor}`
                }}
              />
            </div>
            {/* Top energy burst */}
            <div 
              className={`absolute top-0 left-0 right-0 transition-all duration-[500ms]
                ${animation === 'activated' ? 'h-[200px] opacity-50' : 'h-[50px] opacity-20'}`}
              style={{ 
                background: `linear-gradient(to bottom, ${accentColor}, transparent)`
              }}
            />
          </div>
        );

      // LET GO - Abstract shoulder shapes that rise and fall with sighing release
      case 'shoulder-release':
        return (
          <div className="absolute inset-0 flex items-center justify-center overflow-hidden">
            {/* Left shoulder arc */}
            <div 
              className={`absolute transition-all ease-out
                ${animation === 'lift' ? 'translate-y-[-30px] duration-[3000ms]' : 
                  animation === 'lift-higher' ? 'translate-y-[-60px] duration-[2000ms]' :
                  animation === 'hold-high' ? 'translate-y-[-60px] duration-[500ms]' :
                  animation === 'release-sigh' ? 'translate-y-[40px] duration-[2500ms]' :
                  animation === 'soften' ? 'translate-y-[20px] duration-[3000ms]' :
                  'translate-y-0 duration-[2000ms]'}`}
              style={{
                left: '15%',
                top: '35%',
                width: '180px',
                height: '180px',
                borderRadius: '50% 50% 30% 30%',
                background: `radial-gradient(ellipse at center, ${glowColor} 0%, ${accentColor} 50%, transparent 80%)`,
                boxShadow: animation === 'lift-higher' || animation === 'hold-high' 
                  ? `0 0 60px 30px ${accentColor}` 
                  : animation === 'release-sigh' 
                  ? `0 30px 60px 20px ${accentColor}` 
                  : `0 0 40px 20px ${accentColor}`,
                opacity: animation === 'release-sigh' || animation === 'soften' ? 0.4 : 0.6
              }}
            />
            {/* Right shoulder arc */}
            <div 
              className={`absolute transition-all ease-out
                ${animation === 'lift' ? 'translate-y-[-30px] duration-[3000ms]' : 
                  animation === 'lift-higher' ? 'translate-y-[-60px] duration-[2000ms]' :
                  animation === 'hold-high' ? 'translate-y-[-60px] duration-[500ms]' :
                  animation === 'release-sigh' ? 'translate-y-[40px] duration-[2500ms]' :
                  animation === 'soften' ? 'translate-y-[20px] duration-[3000ms]' :
                  'translate-y-0 duration-[2000ms]'}`}
              style={{
                right: '15%',
                top: '35%',
                width: '180px',
                height: '180px',
                borderRadius: '50% 50% 30% 30%',
                background: `radial-gradient(ellipse at center, ${glowColor} 0%, ${accentColor} 50%, transparent 80%)`,
                boxShadow: animation === 'lift-higher' || animation === 'hold-high' 
                  ? `0 0 60px 30px ${accentColor}` 
                  : animation === 'release-sigh' 
                  ? `0 30px 60px 20px ${accentColor}` 
                  : `0 0 40px 20px ${accentColor}`,
                opacity: animation === 'release-sigh' || animation === 'soften' ? 0.4 : 0.6
              }}
            />
            {/* Central body/spine energy line */}
            <div 
              className={`absolute left-1/2 -translate-x-1/2 transition-all duration-[2000ms]
                ${animation === 'release-sigh' || animation === 'soften' ? 'opacity-70' : 'opacity-40'}`}
              style={{
                top: '40%',
                width: '4px',
                height: animation === 'release-sigh' ? '250px' : '180px',
                background: `linear-gradient(to bottom, ${glowColor}, ${accentColor}, transparent)`,
                borderRadius: '2px'
              }}
            />
            {/* Breath/energy release particles during sigh */}
            {animation === 'release-sigh' && [...Array(8)].map((_, i) => (
              <div
                key={i}
                className="absolute animate-float-down"
                style={{
                  left: `${35 + i * 4}%`,
                  top: '45%',
                  width: '8px',
                  height: '8px',
                  borderRadius: '50%',
                  background: glowColor,
                  opacity: 0.6,
                  animationDelay: `${i * 0.2}s`,
                  animationDuration: '3s'
                }}
              />
            ))}
            {/* Soft glow at center chest area */}
            <div 
              className={`absolute left-1/2 -translate-x-1/2 rounded-full transition-all duration-[2000ms]
                ${animation === 'lift-higher' || animation === 'hold-high' ? 'w-[100px] h-[100px] opacity-50' :
                  animation === 'release-sigh' ? 'w-[200px] h-[200px] opacity-30' :
                  'w-[80px] h-[80px] opacity-25'}`}
              style={{
                top: '42%',
                background: `radial-gradient(circle, ${glowColor} 0%, ${accentColor} 40%, transparent 70%)`
              }}
            />
          </div>
        );

      // HEART - Warm heart-space visualization with expanding gratitude
      case 'heart-expand':
        return (
          <div className="absolute inset-0 flex items-center justify-center overflow-hidden">
            {/* Belly breath indicator */}
            <div 
              className={`absolute rounded-full transition-all ease-in-out
                ${animation === 'belly-breathe' ? 'w-[120px] h-[120px] opacity-40 duration-[4000ms]' :
                  animation === 'rise-to-heart' ? 'w-[80px] h-[80px] opacity-20 translate-y-[-80px] duration-[3000ms]' :
                  'w-[60px] h-[60px] opacity-0 duration-[2000ms]'}`}
              style={{
                top: '60%',
                left: '50%',
                transform: `translateX(-50%) ${animation === 'rise-to-heart' ? 'translateY(-80px)' : ''}`,
                background: `radial-gradient(circle, ${glowColor} 0%, ${accentColor} 60%, transparent 80%)`
              }}
            />
            {/* Main heart glow - center of chest */}
            <div 
              className={`absolute rounded-full transition-all ease-out
                ${animation === 'heart-expand' ? 'w-[180px] h-[180px] opacity-50 duration-[3000ms]' :
                  animation === 'heart-glow' ? 'w-[220px] h-[220px] opacity-60 duration-[2000ms]' :
                  animation === 'heart-radiate' ? 'w-[260px] h-[260px] opacity-55 duration-[2000ms]' :
                  animation === 'heart-fill' ? 'w-[280px] h-[280px] opacity-65 duration-[3000ms]' :
                  animation === 'heart-spread' ? 'w-[350px] h-[350px] opacity-45 duration-[4000ms]' :
                  animation === 'heart-rest' ? 'w-[300px] h-[300px] opacity-50 duration-[2000ms]' :
                  animation === 'rise-to-heart' ? 'w-[100px] h-[100px] opacity-30 duration-[3000ms]' :
                  'w-[80px] h-[80px] opacity-20 duration-[2000ms]'}`}
              style={{
                top: '38%',
                left: '50%',
                transform: 'translateX(-50%)',
                background: `radial-gradient(circle, ${glowColor} 0%, ${accentColor} 50%, transparent 75%)`,
                boxShadow: animation === 'heart-fill' || animation === 'heart-spread' 
                  ? `0 0 100px 50px ${accentColor}` 
                  : `0 0 60px 30px ${accentColor}`
              }}
            />
            {/* Inner heart core - bright warm center */}
            <div 
              className={`absolute rounded-full transition-all duration-[2000ms]
                ${animation === 'heart-glow' || animation === 'heart-radiate' || animation === 'heart-fill' 
                  ? 'w-[60px] h-[60px] opacity-80' :
                  animation === 'heart-spread' || animation === 'heart-rest' 
                  ? 'w-[80px] h-[80px] opacity-70' :
                  'w-[40px] h-[40px] opacity-40'}`}
              style={{
                top: '40%',
                left: '50%',
                transform: 'translateX(-50%)',
                background: `radial-gradient(circle, rgba(255,255,255,0.9) 0%, ${glowColor} 50%, transparent 80%)`
              }}
            />
            {/* Radiating love waves during gratitude */}
            {(animation === 'heart-radiate' || animation === 'heart-fill' || animation === 'heart-spread') && 
              [...Array(4)].map((_, i) => (
                <div 
                  key={i}
                  className="absolute rounded-full animate-heart-wave"
                  style={{
                    top: '38%',
                    left: '50%',
                    transform: 'translateX(-50%)',
                    width: '150px',
                    height: '150px',
                    border: `2px solid ${accentColor}`,
                    opacity: 0.4,
                    animationDelay: `${i * 1}s`,
                    animationDuration: '4s'
                  }}
                />
              ))
            }
            {/* Spreading warmth to body edges */}
            {animation === 'heart-spread' && (
              <>
                <div 
                  className="absolute animate-spread-left"
                  style={{
                    top: '35%',
                    left: '50%',
                    width: '100px',
                    height: '8px',
                    borderRadius: '4px',
                    background: `linear-gradient(to left, ${glowColor}, transparent)`,
                    opacity: 0.5
                  }}
                />
                <div 
                  className="absolute animate-spread-right"
                  style={{
                    top: '35%',
                    right: '50%',
                    width: '100px',
                    height: '8px',
                    borderRadius: '4px',
                    background: `linear-gradient(to right, ${glowColor}, transparent)`,
                    opacity: 0.5
                  }}
                />
              </>
            )}
            {/* Soft ambient particles */}
            {[...Array(6)].map((_, i) => (
              <div
                key={i}
                className={`absolute rounded-full animate-float-gentle
                  ${animation === 'heart-glow' || animation === 'heart-radiate' ? 'opacity-40' : 'opacity-20'}`}
                style={{
                  width: '6px',
                  height: '6px',
                  background: glowColor,
                  left: `${30 + i * 8}%`,
                  top: `${35 + (i % 3) * 5}%`,
                  animationDelay: `${i * 0.5}s`,
                  animationDuration: '5s'
                }}
              />
            ))}
          </div>
        );

      // BATHE - Nature slideshow with smooth crossfades
      case 'nature-bathe':
        const currentSlideIndex = currentPhase?.slideIndex || 0;
        const slides = resetConfig?.slides || [];
        return (
          <div className="absolute inset-0 overflow-hidden">
            {/* Nature slideshow with crossfade */}
            {slides.map((slideUrl, index) => (
              <div
                key={index}
                className="absolute inset-0 transition-opacity ease-in-out"
                style={{
                  backgroundImage: `url(${slideUrl})`,
                  backgroundSize: 'cover',
                  backgroundPosition: 'center',
                  opacity: index === currentSlideIndex ? 1 : 0,
                  transitionDuration: '3000ms',
                  // Subtle zoom animation on active slide
                  transform: index === currentSlideIndex ? 'scale(1.05)' : 'scale(1)',
                  transitionProperty: 'opacity, transform',
                }}
              />
            ))}
            {/* Strong vignette overlay for text readability */}
            <div 
              className="absolute inset-0"
              style={{
                background: 'radial-gradient(ellipse at center, rgba(0,0,0,0.2) 20%, rgba(0,0,0,0.55) 100%)'
              }}
            />
            {/* Stronger bottom gradient for text area */}
            <div 
              className="absolute bottom-0 left-0 right-0 h-2/5"
              style={{
                background: 'linear-gradient(to top, rgba(0,0,0,0.75) 0%, rgba(0,0,0,0.4) 50%, transparent 100%)'
              }}
            />
            {/* Top subtle gradient for header */}
            <div 
              className="absolute top-0 left-0 right-0 h-1/4"
              style={{
                background: 'linear-gradient(to bottom, rgba(0,0,0,0.4), transparent)'
              }}
            />
            {/* Subtle floating particles */}
            {[...Array(10)].map((_, i) => (
              <div
                key={i}
                className="absolute rounded-full animate-float-gentle opacity-30"
                style={{
                  width: `${4 + (i % 3) * 2}px`,
                  height: `${4 + (i % 3) * 2}px`,
                  background: 'rgba(255,255,255,0.8)',
                  left: `${5 + i * 10}%`,
                  top: `${20 + (i % 5) * 15}%`,
                  animationDelay: `${i * 0.7}s`,
                  animationDuration: `${6 + (i % 3) * 2}s`
                }}
              />
            ))}
          </div>
        );
        
      default:
        return null;
    }
  };

  // Reset experience view - FULL SCREEN IMMERSIVE
  if (activeReset && resetConfig) {
    const Icon = resetConfig.icon;
    const progress = (elapsedTime / resetConfig.audioDuration) * 100;
    const isSilentPhase = currentPhase?.silent;

    return (
      <div 
        className={`fixed inset-0 z-[200] flex flex-col transition-all duration-500 ease-out
                   ${isTransitioning ? 'opacity-0 scale-105' : 'opacity-100 scale-100'}`}
        data-testid={`reset-${activeReset}-experience`}
      >
        {/* Full screen gradient background */}
        <div className={`absolute inset-0 bg-gradient-to-b ${resetConfig.bgGradient}`} />
        
        {/* BIG immersive motion animation */}
        <div className="absolute inset-0">
          {renderMotionAnimation()}
        </div>

        {/* Header - minimal and transparent */}
        <div className="relative z-10 flex items-center justify-between px-6 pt-12 pb-4">
          <button 
            onClick={closeReset}
            className="p-3 rounded-full bg-white/15 hover:bg-white/30 backdrop-blur-md transition-colors border border-white/15 shadow-lg"
            data-testid="reset-close-btn"
            aria-label="Close reset"
          >
            <X className="w-5 h-5 text-white" />
          </button>
          
          <div className="text-center">
            <p className="text-xs text-white/40 uppercase tracking-[0.2em] font-light">{resetConfig.name}</p>
          </div>
          
          <button 
            onClick={toggleVoice}
            className={`p-3 rounded-full backdrop-blur-sm transition-colors ${voiceEnabled ? 'bg-white/20' : 'bg-white/10'}`}
            data-testid="reset-voice-toggle"
          >
            {voiceEnabled ? (
              <Volume2 className="w-5 h-5 text-white" />
            ) : (
              <VolumeX className="w-5 h-5 text-white/50" />
            )}
          </button>
        </div>

        {/* Main content - centered with large text */}
        <div className="relative z-10 flex-1 flex flex-col items-center justify-center px-8">
          {/* For simple-breathe: animation is the focus, text below */}
          {resetConfig.motionType === 'simple-breathe' || resetConfig.motionType === 'original-breathe' ? (
            <>
              {/* Spacer for animation area */}
              <div className="flex-1 flex items-center justify-center min-h-[300px]">
                {/* Animation renders in background */}
              </div>
              
              {/* Text below the circle */}
              <div className="text-center mb-8">
                {isComplete || !currentPhase || !Array.isArray(currentPhase.words) || !currentPhase.words.length ? (
                  <p className="text-2xl md:text-3xl font-light text-white/50 leading-relaxed tracking-wide">
                    {displayText}
                  </p>
                ) : (
                  <SyncedPhrase
                    phrase={currentPhase}
                    nextStart={nextPhraseStart}
                    elapsed={elapsedTime}
                    className="text-2xl md:text-3xl font-light text-white/50 leading-relaxed tracking-wide"
                    testId="reset-phrase"
                  />
                )}
              </div>
            </>
          ) : (
            <>
              {/* Small icon indicator - for non-breathe resets */}
              <div className={`w-16 h-16 rounded-full bg-gradient-to-br ${resetConfig.color} 
                             flex items-center justify-center mb-10 shadow-2xl
                             ${isPlaying && !isComplete && !isSilentPhase ? 'animate-pulse' : ''}`}
                   style={{ boxShadow: `0 0 40px 20px ${resetConfig.accentColor}` }}>
                <Icon className="w-8 h-8 text-white" />
              </div>

              {/* Text display - LARGE and impactful */}
              <div className="text-center min-h-[180px] flex flex-col items-center justify-center max-w-lg">
                {isSilentPhase && !isComplete ? (
                  <div className="flex items-center gap-3">
                    <div className="w-2.5 h-2.5 rounded-full bg-white/30 animate-pulse" />
                    <div className="w-2.5 h-2.5 rounded-full bg-white/30 animate-pulse" style={{ animationDelay: '0.3s' }} />
                    <div className="w-2.5 h-2.5 rounded-full bg-white/30 animate-pulse" style={{ animationDelay: '0.6s' }} />
                  </div>
                ) : (
                  <>
                    {isComplete || !currentPhase || !Array.isArray(currentPhase.words) || !currentPhase.words.length ? (
                      <p className={`text-3xl md:text-4xl font-light mb-6 leading-relaxed tracking-wide ${
                        activeReset === 'bathe'
                          ? 'text-white font-medium drop-shadow-[0_2px_10px_rgba(0,0,0,0.9)] [text-shadow:_0_2px_20px_rgb(0_0_0_/_80%),_0_0_40px_rgb(0_0_0_/_60%)]'
                          : 'text-white/50'
                      }`}>
                        {displayText}
                      </p>
                    ) : (
                      <SyncedPhrase
                        phrase={currentPhase}
                        nextStart={nextPhraseStart}
                        elapsed={elapsedTime}
                        className={`text-3xl md:text-4xl font-light mb-6 leading-relaxed tracking-wide ${
                          activeReset === 'bathe'
                            ? 'text-white font-medium drop-shadow-[0_2px_10px_rgba(0,0,0,0.9)] [text-shadow:_0_2px_20px_rgb(0_0_0_/_80%),_0_0_40px_rgb(0_0_0_/_60%)]'
                            : 'text-white/50'
                        }`}
                        testId="reset-phrase"
                      />
                    )}
                    {currentPhase?.instruction && !isComplete && (
                      <p className={`text-xl md:text-2xl font-medium tracking-wide ${
                        activeReset === 'bathe'
                          ? 'text-white/90 [text-shadow:_0_2px_15px_rgb(0_0_0_/_80%)]'
                          : 'text-white/40'
                      }`}>
                        {currentPhase.instruction}
                      </p>
                    )}
                  </>
                )}
              </div>
            </>
          )}
        </div>

        {/* Progress and controls - bottom */}
        <div className="relative z-10 px-6 pb-12">
          {/* Thin progress bar */}
          <div className="mb-8">
            <div className="h-1 bg-white/10 rounded-full overflow-hidden backdrop-blur-sm">
              <div 
                className={`h-full bg-gradient-to-r ${resetConfig.color} transition-all duration-100`}
                style={{ width: `${progress}%` }}
              />
            </div>
          </div>

          {/* Controls */}
          <div className="flex flex-col items-center justify-center gap-4">
            {isComplete ? (
              <>
                {fromJourney ? (
                  // Coming from Journey - show auto-redirect message
                  <div className="text-center space-y-3">
                    <p className="text-white/60 text-sm animate-pulse">Returning to your journey...</p>
                    <button
                      onClick={() => {
                        recordCompletion(activeReset);
                        closeReset();
                      }}
                      className="text-white/40 text-xs hover:text-white/70 transition-colors"
                    >
                      Continue now
                    </button>
                  </div>
                ) : (
                  // Normal reset - show Done button
                  <button
                    onClick={() => {
                      recordCompletion(activeReset);
                      closeReset();
                    }}
                    className={`px-10 py-4 rounded-full bg-gradient-to-r ${resetConfig.color}
                             text-white font-medium text-lg shadow-2xl hover:opacity-90 transition-all
                             hover:scale-105`}
                    data-testid="reset-done-btn"
                  >
                    Done
                  </button>
                )}
              </>
            ) : (
              <button
                onClick={handleManualPlay}
                className={`w-18 h-18 rounded-full bg-gradient-to-r ${resetConfig.color}
                         flex items-center justify-center shadow-2xl hover:scale-110 transition-transform`}
                style={{ 
                  width: '72px', 
                  height: '72px',
                  boxShadow: `0 0 40px 15px ${resetConfig.accentColor}`
                }}
                data-testid="reset-play-pause-btn"
              >
                {isPlaying ? (
                  <Pause className="w-8 h-8 text-white" />
                ) : (
                  <Play className="w-8 h-8 text-white ml-1" />
                )}
              </button>
            )}
          </div>
        </div>

        {/* Keyframe animations */}
        <style jsx>{`
          @keyframes radiate-ring {
            0% { transform: scale(0.3); opacity: 0.6; }
            100% { transform: scale(2.5); opacity: 0; }
          }
          .animate-radiate-ring { animation: radiate-ring 6s ease-out infinite; }
          
          @keyframes ripple-expand {
            0% { transform: scale(1); opacity: 0.6; }
            100% { transform: scale(8); opacity: 0; }
          }
          .animate-ripple-expand { animation: ripple-expand 7s ease-out infinite; }
          
          @keyframes settle-drop {
            0% { transform: translateY(0) scale(1); opacity: 0.7; }
            100% { transform: translateY(120vh) scale(0.3); opacity: 0; }
          }
          .animate-settle-drop { animation: settle-drop 6s ease-in infinite; }
          
          @keyframes shimmer-subtle {
            0%, 100% { opacity: 0.1; transform: scale(1); }
            50% { opacity: 0.2; transform: scale(1.02); }
          }
          .animate-shimmer-subtle { animation: shimmer-subtle 8s ease-in-out infinite; }
          
          @keyframes stillness-pulse {
            0%, 100% { transform: scale(1); opacity: 0.2; }
            50% { transform: scale(1.1); opacity: 0.3; }
          }
          .animate-stillness-pulse { animation: stillness-pulse 6s ease-in-out infinite; }
          
          @keyframes energy-rise {
            0% { transform: translateY(0) scaleY(1); opacity: 0.8; }
            100% { transform: translateY(-120vh) scaleY(1.5); opacity: 0; }
          }
          .animate-energy-rise { animation: energy-rise 2s ease-out infinite; }
          
          @keyframes fade-in {
            0% { opacity: 0; transform: translateY(10px); }
            100% { opacity: 1; transform: translateY(0); }
          }
          .animate-fade-in { animation: fade-in 1s ease-out forwards; }
          
          @keyframes breathe-pulse {
            0%, 100% { transform: scale(1); opacity: 0.9; }
            50% { transform: scale(1.08); opacity: 1; }
          }
          .animate-breathe-pulse { animation: breathe-pulse 3s ease-in-out infinite; }
          
          @keyframes breathe-glow {
            0%, 100% { transform: scale(1); opacity: 0.7; }
            50% { transform: scale(1.05); opacity: 0.9; }
          }
          .animate-breathe-glow { animation: breathe-glow 3s ease-in-out infinite; }
          
          @keyframes breathe-core {
            0%, 100% { transform: scale(1); opacity: 0.8; }
            50% { transform: scale(1.1); opacity: 1; }
          }
          .animate-breathe-core { animation: breathe-core 3s ease-in-out infinite; }
          
          @keyframes float-down {
            0% { transform: translateY(0); opacity: 0.6; }
            100% { transform: translateY(150px); opacity: 0; }
          }
          .animate-float-down { animation: float-down 3s ease-out forwards; }
          
          @keyframes heart-wave {
            0% { transform: translateX(-50%) scale(1); opacity: 0.5; }
            100% { transform: translateX(-50%) scale(3); opacity: 0; }
          }
          .animate-heart-wave { animation: heart-wave 4s ease-out infinite; }
          
          @keyframes float-gentle {
            0%, 100% { transform: translateY(0); opacity: 0.3; }
            50% { transform: translateY(-20px); opacity: 0.5; }
          }
          .animate-float-gentle { animation: float-gentle 5s ease-in-out infinite; }
          
          @keyframes spread-left {
            0% { width: 0; opacity: 0; }
            50% { width: 200px; opacity: 0.6; }
            100% { width: 300px; opacity: 0; }
          }
          .animate-spread-left { animation: spread-left 4s ease-out forwards; }
          
          @keyframes spread-right {
            0% { width: 0; opacity: 0; }
            50% { width: 200px; opacity: 0.6; }
            100% { width: 300px; opacity: 0; }
          }
          .animate-spread-right { animation: spread-right 4s ease-out forwards; }
        `}</style>
      </div>
    );
  }

  // Selection view
  // When auto-launching from a journey link (e.g. ?fromJourney=true&resetId=expand)
  // we DO NOT want to flash the selection grid for a moment before the
  // reset starts — render a soft navy fade instead.
  if (fromJourney && urlResetId && RESETS[urlResetId]) {
    return (
      <div
        className="min-h-screen flex items-center justify-center"
        style={{ background: "linear-gradient(180deg, #0D1B2A 0%, #1F3148 100%)" }}
        data-testid="reset-fromjourney-loader"
      />
    );
  }

  // SAME bridge for direct deep-links from the EDGE landing
  // (e.g. /networker/resets/pre_call from "Daily Edge"). Avoid flashing
  // the resets grid before the reset opens.
  if (urlResetId && RESETS[urlResetId] && !activeReset) {
    return (
      <div
        className="fixed inset-0 flex items-center justify-center"
        data-testid="edge-deeplink-loader"
        style={{
          background:
            "radial-gradient(ellipse at 50% 30%, #1A1410 0%, #0E0B08 55%, #050402 100%)",
        }}
      >
        <div className="text-center">
          <div className="mx-auto w-3 h-3 rounded-full"
               style={{
                 background: "rgba(217,165,90,0.85)",
                 boxShadow: "0 0 22px rgba(217,165,90,0.55)",
                 animation: "edgePulseR 1.6s ease-in-out infinite",
               }} />
          <p className="mt-5 text-[10px] uppercase tracking-[0.5em] text-amber-500/60">
            The Edge
          </p>
        </div>
        <style>{`@keyframes edgePulseR { 0%,100% { transform: scale(1); opacity: .6 } 50% { transform: scale(1.6); opacity: 1 } }`}</style>
      </div>
    );
  }
  return (
    <div className="min-h-screen bg-gradient-to-b from-slate-900 via-slate-800 to-slate-900">
      <div className="px-6 pt-12 pb-6">
        <div className="flex items-center justify-between mb-6">
          <button 
            onClick={() => navigate(-1)}
            className="flex items-center gap-2 text-slate-400 hover:text-white transition-colors"
            data-testid="resets-back-btn"
          >
            <ArrowLeft className="w-5 h-5" />
            <span>Back</span>
          </button>
          
          {/* Streak Counter */}
          {streak > 0 && (
            <div 
              className="flex items-center gap-2 px-3 py-1.5 rounded-full bg-gradient-to-r from-orange-500/20 to-amber-500/20 border border-orange-500/30"
              data-testid="reset-streak-counter"
            >
              <Flame className="w-4 h-4 text-orange-400" />
              <span className="text-orange-300 font-semibold text-sm">{streak} day{streak !== 1 ? 's' : ''}</span>
            </div>
          )}
        </div>
        
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold text-white mb-1">State Resets</h1>
            <p className="text-slate-400 text-sm">Shift state in 60–180 seconds. One tap.</p>
          </div>
          
          {/* Today's count */}
          {todayCount > 0 && (
            <div className="text-right">
              <p className="text-2xl font-bold text-white">{todayCount}</p>
              <p className="text-xs text-slate-500">today</p>
            </div>
          )}
        </div>
      </div>

      {/* Core Teaching Banner */}
      <div className="px-6 mb-6">
        <div className="p-4 rounded-2xl bg-gradient-to-r from-violet-500/10 to-pink-500/10 border border-violet-500/20">
          <p className="text-white/70 text-sm leading-relaxed text-center">
            When life contracts you, these tools help you <span className="text-violet-400">open</span> again.
          </p>
        </div>
      </div>

      {/* How do you feel? */}
      <div className="px-6 mb-6">
        <button
          onClick={() => setShowSuggestions(!showSuggestions)}
          className="w-full p-4 rounded-2xl bg-white/5 border border-white/10 
                   hover:bg-white/10 transition-colors text-left"
          data-testid="how-feel-btn"
        >
          <p className="text-white font-medium mb-1">How do you feel?</p>
          <p className="text-slate-400 text-sm">Get a personalized recommendation</p>
        </button>

        {showSuggestions && (
          <div className="mt-3 grid grid-cols-3 gap-3">
            {SUGGESTIONS.map((suggestion) => {
              const SugIcon = suggestion.icon;
              const reset = RESETS[suggestion.reset];
              const ResetIcon = reset.icon;
              
              return (
                <button
                  key={suggestion.feeling}
                  onClick={() => startReset(suggestion.reset)}
                  className="p-3 rounded-xl bg-white/5 border border-white/10 
                           hover:bg-white/10 transition-all text-left group"
                  data-testid={`suggestion-${suggestion.feeling.toLowerCase().replace(' ', '-')}`}
                >
                  <div className="flex items-center gap-1.5 mb-2">
                    <SugIcon className="w-3.5 h-3.5 text-slate-400" />
                    <span className="text-xs text-slate-400">{suggestion.feeling}</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <div className={`w-7 h-7 rounded-lg bg-gradient-to-br ${reset.color} 
                                  flex items-center justify-center`}>
                      <ResetIcon className="w-3.5 h-3.5 text-white" />
                    </div>
                    <span className="text-white font-medium text-sm">{reset.name}</span>
                  </div>
                </button>
              );
            })}
          </div>
        )}
      </div>

      {/* Reset buttons */}
      <div className="px-6 pb-24">
        <div className="flex items-baseline justify-between mb-4">
          <p className="text-xs text-slate-500 uppercase tracking-wider">Choose a reset</p>
        </div>

        {/* Category filter tabs (Edge spec) */}
        <div className="flex gap-1.5 mb-5 overflow-x-auto -mx-1 px-1" data-testid="reset-category-tabs">
          {CATEGORY_TABS.map((tab) => {
            const active = categoryFilter === tab.id;
            return (
              <button
                key={tab.id}
                onClick={() => setCategoryFilter(tab.id)}
                className={`px-3.5 py-1.5 text-xs rounded-full whitespace-nowrap transition-all ${
                  active
                    ? 'bg-white text-slate-900 font-medium'
                    : 'bg-white/5 text-slate-400 border border-white/10 hover:bg-white/10'
                }`}
                data-testid={`reset-tab-${tab.id}`}
              >
                {tab.label}
                {tab.id === 'performance' && (
                  <span className="ml-1.5 inline-block w-1.5 h-1.5 rounded-full bg-amber-500" />
                )}
              </button>
            );
          })}
        </div>

        <div className="grid grid-cols-3 gap-3">
          {GRID_ORDER
            .filter((resetId) => {
              const r = RESETS[resetId];
              if (!r) return false;
              if (categoryFilter === 'all') return true;
              return r.category === categoryFilter;
            })
            .map((resetId) => {
            const reset = RESETS[resetId];
            const Icon = reset.icon;
            
            return (
              <button
                key={reset.id}
                onClick={() => startReset(reset.id)}
                className="relative overflow-hidden rounded-2xl p-4 border border-white/10 
                         bg-white/5 hover:bg-white/10 transition-all duration-300 
                         group text-left min-h-[140px]"
                data-testid={`reset-${reset.id}-btn`}
              >
                <div className={`absolute inset-0 bg-gradient-to-br ${reset.color} opacity-0 
                              group-hover:opacity-10 transition-opacity duration-300`} />
                
                <div className="relative z-10 flex flex-col items-center text-center h-full justify-between">
                  <div className={`w-12 h-12 rounded-xl bg-gradient-to-br ${reset.color} 
                                flex items-center justify-center mb-3 shadow-lg
                                group-hover:scale-110 transition-transform duration-300`}>
                    <Icon className="w-6 h-6 text-white" />
                  </div>
                  
                  <div>
                    <h3 className="text-base font-semibold text-white mb-1">{reset.name}</h3>
                    <p className="text-xs text-slate-400 leading-tight">{reset.tagline}</p>
                  </div>
                </div>
              </button>
            );
          })}
        </div>
      </div>
    </div>
  );
};

export default NetworkerResetsPage;

 